import React from 'react';
import { Star, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface BookReviewFiltersProps {
  filterRating: number | null;
  onFilterChange: (rating: number | null) => void;
  sortBy: 'recent' | 'popular';
  onSortChange: (sort: 'recent' | 'popular') => void;
  totalReviews: number;
  filteredCount: number;
  ratingDistribution?: { [key: number]: number };
  availableTags?: string[];
  selectedTags?: string[];
  onTagsChange?: (tags: string[]) => void;
}

export function BookReviewFilters({
  filterRating,
  onFilterChange,
  sortBy,
  onSortChange,
  totalReviews,
  filteredCount,
  availableTags = [],
  selectedTags = [],
  onTagsChange,
}: BookReviewFiltersProps) {
  
  const handleStarClick = (star: number) => {
    // Toggle: if already selected, clear filter; otherwise set to this star
    if (filterRating === star) {
      onFilterChange(null);
    } else {
      onFilterChange(star);
    }
  };

  const handleTagToggle = (tag: string) => {
    if (!onTagsChange) return;
    
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter(t => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const clearAllFilters = () => {
    onFilterChange(null);
    onTagsChange?.([]);
  };

  const hasActiveFilters = filterRating !== null || selectedTags.length > 0;

  return (
    <div className="space-y-4">
      {/* Main filter row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Star Rating Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Filter:</span>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleStarClick(star)}
                  className={cn(
                    "p-0.5 rounded transition-colors hover:scale-110",
                    star <= (filterRating || 0) 
                      ? "text-primary" 
                      : "text-muted-foreground/40 hover:text-muted-foreground"
                  )}
                  aria-label={`Filter ${star}+ stars`}
                >
                  <Star 
                    className={cn(
                      "w-5 h-5",
                      star <= (filterRating || 0) && "fill-current"
                    )} 
                  />
                </button>
              ))}
            </div>
            {filterRating && (
              <span className="text-sm text-muted-foreground">
                {filterRating}+ stars
              </span>
            )}
          </div>

          {/* Sort Options */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Sort:</span>
            <Select value={sortBy} onValueChange={(value) => onSortChange(value as 'recent' | 'popular')}>
              <SelectTrigger className="w-[130px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most Recent</SelectItem>
                <SelectItem value="popular">Most Popular</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Clear all button */}
          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearAllFilters}
              className="h-8 px-2 text-muted-foreground"
            >
              <X className="w-3 h-3 mr-1" />
              Clear
            </Button>
          )}
        </div>

        {/* Result count */}
        {filteredCount !== totalReviews && (
          <span className="text-sm text-muted-foreground">
            Showing {filteredCount} of {totalReviews}
          </span>
        )}
      </div>

      {/* Tag filters */}
      {availableTags.length > 0 && onTagsChange && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Tags:</span>
          <div className="flex flex-wrap gap-1.5">
            {availableTags.slice(0, 8).map((tag) => (
              <Badge
                key={tag}
                variant={selectedTags.includes(tag) ? "default" : "outline"}
                className={cn(
                  "cursor-pointer transition-colors text-xs",
                  selectedTags.includes(tag) 
                    ? "bg-primary text-primary-foreground hover:bg-primary/90" 
                    : "hover:bg-accent"
                )}
                onClick={() => handleTagToggle(tag)}
              >
                {tag}
                {selectedTags.includes(tag) && (
                  <X className="w-3 h-3 ml-1" />
                )}
              </Badge>
            ))}
            {availableTags.length > 8 && (
              <span className="text-xs text-muted-foreground">
                +{availableTags.length - 8} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
